import { createContext, useContext, useState, useEffect, useMemo, useCallback, memo, type ReactNode } from "react";

// Define your feature flags here
type FeatureFlag =
  | "ai_negotiator"
  | "ai_salesman"
  | "semantic_search"
  | "video_listings"
  | "instant_messaging"
  | "crypto_payments"
  | "premium_analytics"
  | "survey_missions"
  | "social_login"
  | "dark_mode_v2"
  | "new_onboarding";

interface FeatureFlags {
  [key: string]: boolean;
}

// Split contexts for granular subscriptions
interface FeatureFlagsStateContextType {
  flags: FeatureFlags;
  loading: boolean;
}

interface FeatureFlagsActionsContextType {
  isEnabled: (flag: FeatureFlag) => boolean;
  setFlag: (flag: FeatureFlag, enabled: boolean) => void;
}

const defaultFlags: FeatureFlags = {
  ai_negotiator: true,
  ai_salesman: true,
  semantic_search: true,
  video_listings: false,
  instant_messaging: true,
  crypto_payments: false,
  premium_analytics: true,
  survey_missions: true,
  social_login: true,
  dark_mode_v2: false,
  new_onboarding: false,
};

const STORAGE_KEY = "listo_feature_flags_override";

const FeatureFlagsStateContext = createContext<FeatureFlagsStateContextType | null>(null);
const FeatureFlagsActionsContext = createContext<FeatureFlagsActionsContextType | null>(null);

interface FeatureFlagsProviderProps {
  children: ReactNode;
  initialFlags?: Partial<FeatureFlags>;
  remoteEndpoint?: string;
}

export function FeatureFlagsProvider({ 
  children, 
  initialFlags,
  remoteEndpoint 
}: FeatureFlagsProviderProps) {
  const [flags, setFlags] = useState<FeatureFlags>(() => {
    // Spread defaultFlags first, then override with initialFlags (filtering out undefined)
    const merged: FeatureFlags = { ...defaultFlags };
    
    if (initialFlags) {
      for (const [key, value] of Object.entries(initialFlags)) {
        if (value !== undefined) {
          merged[key] = value;
        }
      }
    }

    // Apply local overrides
    if (typeof window !== "undefined") {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const overrides = JSON.parse(stored) as Record<string, boolean>;
          for (const [key, value] of Object.entries(overrides)) {
            if (typeof value === "boolean") {
              merged[key] = value;
            }
          }
        }
      } catch {
        // Ignore parse errors
      }
    }

    return merged;
  });

  const [loading, setLoading] = useState(!!remoteEndpoint);

  // Fetch remote flags
  useEffect(() => {
    if (!remoteEndpoint) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    
    fetch(remoteEndpoint, { signal: controller.signal })
      .then((res) => res.json())
      .then((remoteFlags) => {
        setFlags((prev) => ({ ...prev, ...remoteFlags }));
      })
      .catch(() => {
        // Use local flags on error
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [remoteEndpoint]);

  // Memoize isEnabled to use current flags via closure
  const isEnabled = useCallback(
    (flag: FeatureFlag): boolean => flags[flag] ?? false,
    [flags]
  );

  const setFlag = useCallback((flag: FeatureFlag, enabled: boolean) => {
    setFlags((prev) => {
      if (prev[flag] === enabled) return prev;
      
      const updated = { ...prev, [flag]: enabled };

      // Save override to localStorage
      if (typeof window !== "undefined") {
        try {
          const existing = localStorage.getItem(STORAGE_KEY);
          const overrides = existing ? JSON.parse(existing) : {};
          overrides[flag] = enabled;
          localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
        } catch {
          // Ignore storage errors
        }
      }

      return updated;
    });
  }, []);

  // Memoize context values
  const stateValue = useMemo<FeatureFlagsStateContextType>(
    () => ({ flags, loading }),
    [flags, loading]
  );

  const actionsValue = useMemo<FeatureFlagsActionsContextType>(
    () => ({ isEnabled, setFlag }),
    [isEnabled, setFlag]
  );

  return (
    <FeatureFlagsActionsContext.Provider value={actionsValue}>
      <FeatureFlagsStateContext.Provider value={stateValue}>
        {children}
      </FeatureFlagsStateContext.Provider>
    </FeatureFlagsActionsContext.Provider>
  );
}

// Hooks for granular access
export function useFeatureFlagsState() {
  const context = useContext(FeatureFlagsStateContext);
  if (!context) {
    throw new Error("useFeatureFlagsState must be used within a FeatureFlagsProvider");
  }
  return context;
}

export function useFeatureFlagsActions() {
  const context = useContext(FeatureFlagsActionsContext);
  if (!context) {
    throw new Error("useFeatureFlagsActions must be used within a FeatureFlagsProvider");
  }
  return context;
}

// Combined hook for backwards compatibility
export function useFeatureFlags() {
  const state = useFeatureFlagsState();
  const actions = useFeatureFlagsActions();
  return { ...state, ...actions };
}

// Optimized hook for single flag - creates stable subscription
export function useFeatureFlag(flag: FeatureFlag): boolean {
  const { flags } = useFeatureFlagsState();
  return flags[flag] ?? false;
}

// Loading state hook
export function useFeatureFlagsLoading(): boolean {
  return useFeatureFlagsState().loading;
}

// Memoized component for conditional rendering
interface FeatureGateProps {
  flag: FeatureFlag;
  children: ReactNode;
  fallback?: ReactNode;
}

export const FeatureGate = memo(function FeatureGate({ 
  flag, 
  children, 
  fallback = null 
}: FeatureGateProps) {
  const enabled = useFeatureFlag(flag);
  return enabled ? <>{children}</> : <>{fallback}</>;
});

// Utility to check multiple flags at once
export function useFeatureFlagsMultiple<T extends FeatureFlag>(flags: T[]): Record<T, boolean> {
  const { flags: allFlags } = useFeatureFlagsState();
  return useMemo(
    () => flags.reduce((acc, flag) => {
      acc[flag] = allFlags[flag] ?? false;
      return acc;
    }, {} as Record<T, boolean>),
    [allFlags, flags]
  );
}

export type { FeatureFlag, FeatureFlags };
